import {
	type Dispatch,
	type KeyboardEventHandler,
	type RefObject,
	type SetStateAction,
	useEffect,
	useState,
} from 'react';

type UseKeyboardNavigationProps = {
	query: string;
	resultsCount: number;
	resultRefs: RefObject<(HTMLAnchorElement | null)[]>;
	onShowDefaultResults?: () => void;
	onShowDefaultResultsAndJumpToEnd?: () => void;
};

export const useKeyboardNavigation = ({
	query,
	resultsCount,
	resultRefs,
	onShowDefaultResults,
	onShowDefaultResultsAndJumpToEnd,
}: UseKeyboardNavigationProps): {
	activeIndex: number;
	handleInputKeyDown: KeyboardEventHandler<HTMLInputElement>;
	setActiveIndex: Dispatch<SetStateAction<number>>;
} => {
	const [activeIndex, setActiveIndex] = useState(-1);

	// Reset when query changes (new set of results).
	useEffect(() => {
		setActiveIndex(-1);
	}, [query]);

	const handleInputKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
		if (e.key === 'ArrowDown') {
			e.preventDefault();
			// If query is empty and no results, trigger default results
			if (!query && resultsCount <= 0) {
				onShowDefaultResults?.();
				return;
			}
			// If we have results, navigate
			if (resultsCount > 0) {
				setActiveIndex((prev) => {
					if (prev < 0) {
						return 0;
					}
					return Math.min(prev + 1, resultsCount - 1);
				});
			}
			return;
		}

		if (e.key === 'ArrowUp') {
			e.preventDefault();
			// If query is empty and no results, trigger default results and jump to end
			if (!query && resultsCount <= 0) {
				onShowDefaultResultsAndJumpToEnd?.();
				return;
			}
			// Only navigate if we have results
			if (resultsCount > 0) {
				setActiveIndex((prev) => {
					if (prev < 0) {
						return resultsCount - 1;
					}
					return Math.max(prev - 1, 0);
				});
			}
			return;
		}

		if (e.key === 'Home') {
			e.preventDefault();
			// Jump to first result if we have results
			if (resultsCount > 0) {
				setActiveIndex(0);
			}
			return;
		}

		if (e.key === 'End') {
			e.preventDefault();
			// Jump to last result if we have results
			if (resultsCount > 0) {
				setActiveIndex(resultsCount - 1);
			}
			return;
		}

		if (e.key === 'Enter') {
			if (activeIndex < 0) {
				return;
			}
			e.preventDefault();
			const target = resultRefs.current?.[activeIndex];
			target?.click();
			return;
		}
	};

	return {
		activeIndex,
		handleInputKeyDown,
		setActiveIndex,
	};
};
